import { useRef } from 'react';
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { useImmer } from 'use-immer';

import { mapTableSorterToSearchQuerySorter } from './filterHelpers';
import { Filter } from './filters.type';
import { SearchQueryCriteriaType, SearchQueryCriteriaValueType } from './types';

export const useTableDataFilters = (defaultFilter?: Filter, storedPagingData?: string) => {
  const pageIndex = useRef(1);
  const [pagingData, setPagingData] = useImmer({ pagingFrom: 0, pageSize: 5 });
  const defaultState: Filter = defaultFilter ?? {
    name: '',
    criteria: [],
    sort: [],
    isDefault: true,
    id: 0,
  };
  const [currentFilter, setCurrentFilter] = useImmer(defaultState);

  const resetPagination = () => {
    pageIndex.current = 1;
    setPagingData((draft) => {
      draft.pagingFrom = 0;
    });
  };

  const handleChangeSortingAndPagination = (
    { current, pageSize, total }: TablePaginationConfig,
    filters?: Record<string, FilterValue | null>,
    sorter?: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[]
  ) => {
    if (current && pageSize && total) {
      const from = current * pageSize - pageSize;
      setPagingData((draft) => {
        draft.pagingFrom = from;
        draft.pageSize = pageSize;
      });
      pageIndex.current = current;

      if (storedPagingData) {
        localStorage.setItem(storedPagingData, JSON.stringify({ current, pageSize, total }));
      }
    }

    if (sorter) {
      setCurrentFilter((draft) => {
        draft.sort = mapTableSorterToSearchQuerySorter(sorter) as never;
      });
    }
  };

  const handleChangeCriteria = (values: SearchQueryCriteriaType[]) => {
    setCurrentFilter((draft) => {
      draft.criteria = values;
    });
    resetPagination();
  };

  const handleChangeFilter = (filter: Filter) => {
    setCurrentFilter(() => {
      return filter;
    });
    resetPagination();
  };

  const resetFilterToDefault = () => {
    setCurrentFilter(() => {
      return defaultState;
    });
    resetPagination();
  };

  const clearFilters = () => {
    setCurrentFilter((draft) => {
      draft.criteria = [];
      draft.sort = [];
    });
    resetPagination();
  };

  const addCriteria = (newCriteria: SearchQueryCriteriaType, overwrite: boolean) => {
    if (!newCriteria.value) return;
    setCurrentFilter((draft) => {
      if (overwrite) {
        draft.criteria = draft.criteria.filter((criterion) => criterion.field !== newCriteria.field);
      }
      draft.criteria = [...draft.criteria, newCriteria];
    });
    resetPagination();
  };

  const deleteCriteria = (field: string, value?: SearchQueryCriteriaValueType) => {
    setCurrentFilter((draft) => {
      draft.criteria = draft.criteria.filter((criteria) => {
        return criteria.field !== field || (value && criteria.value !== value);
      });
    });
    resetPagination();
  };

  return {
    currentFilter,
    pageNumber: pageIndex.current,
    pagingFrom: pagingData.pagingFrom,
    pageSize: pagingData.pageSize,
    resetFilterToDefault,
    clearFilters,
    handleChangeSortingAndPagination,
    handleChangeCriteria,
    handleChangeFilter,
    addCriteria,
    deleteCriteria,
  };
};
